import { z } from 'zod';
import { LOCALES } from '../constants.js';
export const localeSchema = z.enum(LOCALES);
const slug = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Only lowercase letters, numbers and dashes');
/** Shared with products.ts. */
export const slugSchema = slug;
/* ═══════════════════════════════════════════════════════════════════════
 * IMPORTANT — why every entity below has THREE schemas
 *
 *   xBase    field rules, NO defaults
 *   xInput   base + defaults        → for CREATE
 *   xPatch   base.partial()         → for PARTIAL UPDATE
 *
 * `.partial()` makes keys optional but does NOT remove `.default()`. So
 * `offerInputSchema.partial().parse({ priceGrosz: 15000 })` returns every
 * other field filled in with its default — and using that to build an
 * UPDATE silently wipes the description, the bullets, the display order and
 * the featured flag.
 *
 * That bug shipped once. Changing a price erased the offer. Patch schemas
 * must never carry defaults.
 * ═══════════════════════════════════════════════════════════════════════ */
/* ── Offers ────────────────────────────────────────────────────────────
 * The lesson types and prices on /lekcje. Diana edits these herself, which
 * is the whole point: raising a price is a ten-second job, not a deploy.
 */
/**
 * What an offer is, as far as the booking page cares:
 *   trial    the free intro call — books the slot straight away
 *   single   one paid lesson
 *   package  several lessons; the student requests it and Diana agrees a
 *            regular weekly slot with them before anything is confirmed
 *   contact  nothing to book — group lessons, the speaking club. The button
 *            sends the visitor to Telegram or the contact form instead
 */
export const OFFER_KINDS = ['trial', 'single', 'package', 'contact'];
export const OFFER_KIND_LABELS = {
    trial: 'Rozmowa poznawcza',
    single: 'Pojedyncza lekcja',
    package: 'Pakiet',
    contact: 'Kontakt (bez rezerwacji)',
};
/** Kinds the booking calendar accepts. A `contact` offer never books a slot. */
export const BOOKABLE_OFFER_KINDS = ['trial', 'single', 'package'];
export function isBookableOfferKind(kind) {
    return BOOKABLE_OFFER_KINDS.includes(kind);
}
/**
 * Where a `contact` offer's button may point: an absolute http(s) link
 * (Telegram, a form) or a path on this site. Empty means "use the Telegram
 * link from settings, or the contact page if there is none".
 */
const ctaUrl = z
    .string()
    .max(500)
    .refine((v) => v === '' || v.startsWith('/') || /^https?:\/\/\S+$/.test(v), 'Podaj pełny adres (https://…) albo ścieżkę (/kontakt)');
/** Shared with products.ts. */
export const ctaUrlSchema = ctaUrl;
const offerBase = z.object({
    locale: localeSchema,
    slug,
    kind: z.enum(OFFER_KINDS),
    /** How many lessons a package contains. 1 for anything that isn't one. */
    lessons: z.number().int().positive().max(500),
    title: z.string().min(1).max(120),
    subtitle: z.string().max(200),
    description: z.string(),
    /** "What's included" bullets. */
    includes: z.array(z.string().min(1)),
    /** Minutes. 0 = no duration line on the card (group lessons with no fixed length). */
    durationMin: z.number().int().nonnegative().max(600),
    /** Integer grosze. 12000 = 120 zł. */
    priceGrosz: z.number().int().nonnegative(),
    /** Optional "per person" note for pair/group offers. */
    priceNote: z.string().max(120),
    /** Replaces the price on the card — "Dowiedz się o dostępności" for offers with no fixed price. */
    priceLabel: z.string().max(80),
    /** Button text. Empty = a sensible default for the kind. */
    ctaLabel: z.string().max(60),
    /** Where the button goes for `contact` offers. Ignored for bookable kinds. */
    ctaUrl,
    /** Optional photo across the top of the card. */
    coverImageId: z.uuid().nullable(),
    order: z.number().int(),
    active: z.boolean(),
    /** Highlights one card on the pricing section. */
    featured: z.boolean(),
});
export const offerInputSchema = offerBase.extend({
    kind: offerBase.shape.kind.default('single'),
    lessons: offerBase.shape.lessons.default(1),
    subtitle: offerBase.shape.subtitle.default(''),
    description: offerBase.shape.description.default(''),
    includes: offerBase.shape.includes.default([]),
    priceNote: offerBase.shape.priceNote.default(''),
    priceLabel: offerBase.shape.priceLabel.default(''),
    ctaLabel: offerBase.shape.ctaLabel.default(''),
    ctaUrl: offerBase.shape.ctaUrl.default(''),
    coverImageId: offerBase.shape.coverImageId.default(null),
    order: offerBase.shape.order.default(0),
    active: offerBase.shape.active.default(true),
    featured: offerBase.shape.featured.default(false),
});
/** No defaults — an absent key means "leave it alone". */
export const offerPatchSchema = offerBase.partial();
/**
 * What the API hands out. `coverUrl` is resolved from `coverImageId` on the
 * server, so the site never has to look a picture up by id.
 */
export const offerSchema = offerInputSchema.extend({
    id: z.uuid(),
    coverUrl: z.string().nullable().default(null),
});
/* ── Testimonials ──────────────────────────────────────────────────────
 * Named reviews with country and level. "Anonimowy uczeń" is worth nothing —
 * see docs/01-WEBSITE.md §2.
 */
const testimonialBase = z.object({
    locale: localeSchema,
    studentName: z.string().min(1).max(120),
    country: z.string().max(80),
    level: z.string().max(20),
    text: z.string().min(1),
    photoId: z.uuid().nullable(),
    featured: z.boolean(),
    order: z.number().int(),
});
export const testimonialInputSchema = testimonialBase.extend({
    country: testimonialBase.shape.country.default(''),
    level: testimonialBase.shape.level.default(''),
    photoId: testimonialBase.shape.photoId.default(null),
    featured: testimonialBase.shape.featured.default(false),
    order: testimonialBase.shape.order.default(0),
});
export const testimonialPatchSchema = testimonialBase.partial();
export const testimonialSchema = testimonialInputSchema.extend({ id: z.uuid() });
/* ── FAQ ───────────────────────────────────────────────────────────────*/
const faqBase = z.object({
    locale: localeSchema,
    question: z.string().min(1).max(300),
    answer: z.string().min(1),
    order: z.number().int(),
});
export const faqInputSchema = faqBase.extend({ order: faqBase.shape.order.default(0) });
export const faqPatchSchema = faqBase.partial();
export const faqSchema = faqInputSchema.extend({ id: z.uuid() });
/* ── Pages ─────────────────────────────────────────────────────────────
 * Per-page copy and, critically, the SEO/OG fields. The OG image is what
 * makes a link posted on Instagram look like something worth clicking.
 */
const pageBase = z.object({
    locale: localeSchema,
    slug,
    title: z.string().min(1).max(200),
    /** Free-form per-page copy: headings, intro paragraphs, button labels. */
    content: z.record(z.string(), z.string()),
    seoTitle: z.string().max(70),
    seoDescription: z.string().max(200),
    ogImageId: z.uuid().nullable(),
    published: z.boolean(),
});
export const pageInputSchema = pageBase.extend({
    content: pageBase.shape.content.default({}),
    seoTitle: pageBase.shape.seoTitle.default(''),
    seoDescription: pageBase.shape.seoDescription.default(''),
    ogImageId: pageBase.shape.ogImageId.default(null),
    published: pageBase.shape.published.default(true),
});
export const pagePatchSchema = pageBase.partial();
export const pageSchema = pageInputSchema.extend({ id: z.uuid() });
/* ── Media ─────────────────────────────────────────────────────────────*/
export const mediaSchema = z.object({
    id: z.uuid(),
    key: z.string().min(1),
    mime: z.string().min(1),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    sizeBytes: z.number().int().nonnegative(),
    alt: z.string().default(''),
    createdAt: z.coerce.date(),
});
/* ── Settings ──────────────────────────────────────────────────────────
 * Global, non-page content: contact details, social links, the "free slots"
 * line, booking rules. Stored as one JSON blob per key per locale.
 */
export const settingsSchema = z.object({
    key: z.string().min(1).max(80),
    locale: localeSchema.nullable(),
    value: z.unknown(),
});
